import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Datagrid,
  FunctionField,
  ListContextProvider,
  NumberField,
  Pagination,
  SimpleList,
  TextField,
  Title,
  useList,
  useGetList,
  useStore,
} from "react-admin";
import CustomFilter from "features/CustomFilter";
import { ALL_OPTIONS } from "features/filters/constants";
import { administrationRequests } from "api/administrationApi";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import dataProvider from "server/dataProvider";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { Currency, MOBILE_BREAKPOINT, ScholarshipStatuses, ScholarshipTypes } from "utils/constants";
import { scholarshipRequests } from "api/scholarshipApi";
import ActionButtons from "./ActionButtons/ActionButtons";
import ScholarshipAmountSection from "./ScholarshipAmountSection";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import FinalizeDialog from "./ActionButtons/FinalizeDialog";
import { ScholarshipCategoryChoices, ScholarshipStatusesChoices, SchorashipTypeChoices } from "utils/choices";
import { useNotifyError } from "utils/useNotifyError";

const STORE_KEY = "approvedScholarships.filters";

const ListApprovedScholarships = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notifyError = useNotifyError();

  const [filters, setFilters] = useStore(STORE_KEY, null);

  // Pre-filter draft, committed to `filters` on "Покажи".
  const [scholarshipType, setScholarshipType] = useState(filters?.scholarshipType ?? null);
  const [scholarshipCategory, setScholarshipCategory] = useState(filters?.scholarshipCategory ?? null);
  const [structuralUnitId, setStructuralUnitId] = useState(filters?.structuralUnitId ?? null);

  const { data: structuralUnits } = useGetList(administrationRequests.getStructuralUnitsSelect(), {
    ...ALL_OPTIONS,
    filter: { filterByUser: true, onlyProvidingCampaign: true },
  });

  const changeScholarshipType = value => {
    setScholarshipType(value);
    setScholarshipCategory(null);
    setStructuralUnitId(null);
  };
  const changeScholarshipCategory = value => {
    setScholarshipCategory(value);
    setStructuralUnitId(null);
  };
  const filterQuery = Object.fromEntries(
    Object.entries({ scholarshipType, scholarshipCategory, structuralUnitId }).filter(
      ([, value]) => value != null && value !== "",
    ),
  );

  const [approvedApplications, setApprovedApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [budgetInfo, setBudgetInfo] = useState(null);
  const [isApplicationProcessActive, setApplicationProcessActive] = useState(null);
  const [isCampaignActive, setIsCampaignActive] = useState(null);
  const [scholarshipAmount, setScholarshipAmount] = useState(null);
  const [campaignId, setCampaignId] = useState(null);

  const [excludedIds, setExcludedIds] = useState([]);
  const excludedIdsRef = useRef([]);
  const [allSeenIds, setAllSeenIds] = useState([]);
  const seenIdsRef = useRef([]);
  const requestIdRef = useRef(0);

  const resetSelection = useCallback(() => {
    excludedIdsRef.current = [];
    setExcludedIds([]);
    seenIdsRef.current = [];
    setAllSeenIds([]);
  }, []);

  const handleSearch = selectedFilters => {
    setPage(1);
    setFilters(selectedFilters);
    resetSelection();
  };

  const fetchApprovedApplications = useCallback(async () => {
    // Guard against out-of-order responses: only the latest request may apply its result.
    const requestId = ++requestIdRef.current;
    try {
      const { data } = await dataProvider.customRequest(
        scholarshipRequests.getApprovedScholarshipApplications({ ...filters, CurrentPage: page, PerPage: perPage }),
        null,
        "GET",
      );
      if (requestId !== requestIdRef.current) return;
      setApprovedApplications(data?.approvedApplications?.items ?? []);
      setTotal(data?.approvedApplications?.totalItems ?? 0);
      setBudgetInfo(data?.budgetInfo);
      setApplicationProcessActive(data?.isApplicationProcessActive);
      setIsCampaignActive(data?.isCampaignActive);
      setScholarshipAmount(data?.scholarshipAmount);
      setCampaignId(data?.campaignId);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      notifyError(err);
    }
  }, [filters, notifyError, page, perPage]);

  useEffect(() => {
    if (!filters) return;
    fetchApprovedApplications();
  }, [filters, fetchApprovedApplications]);

  const baseListContext = useList({
    data: approvedApplications,
    ids: approvedApplications?.map(r => r.id),
    isLoading: false,
    page,
    perPage,
    setPage,
    setPerPage,
  });

  const handleToggleItem = id => {
    baseListContext.onToggleItem(id);
    setExcludedIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      excludedIdsRef.current = next;
      return next;
    });
  };

  // Reset to page 1 when the page size changes — otherwise a current page beyond the
  // new page count returns an empty result and <Pagination> unmounts (no way back).
  const handlePerPageChange = value => {
    setPerPage(value);
    setPage(1);
  };

  const listContext = {
    ...baseListContext,
    total,
    page,
    perPage,
    setPage,
    setPerPage: handlePerPageChange,
    onSelect: baseListContext.onSelect,
    onToggleItem: handleToggleItem,
    onUnselectItems: baseListContext.onUnselectItems,
  };

  useEffect(() => {
    if (approvedApplications?.length > 0) {
      const pageIds = approvedApplications.map(r => r.id);
      // Rows seen for the first time on this page load (manual toggles on already-seen rows must survive revisits).
      const newIds = pageIds.filter(id => !seenIdsRef.current.includes(id));

      // Only "Approved" (status 1) rows are checked by default. Seed the newly-seen non-approved rows into
      // excludedIds so the submit payload (allSeenIds − excludedIds) matches the visual selection — otherwise
      // untouched non-approved rows would still be sent as "Approved".
      const newExclusions = approvedApplications
        .filter(r => newIds.includes(r.id) && r.status !== ScholarshipStatuses.Approved)
        .map(r => r.id);
      if (newExclusions.length) {
        const nextExcluded = [...excludedIdsRef.current, ...newExclusions];
        excludedIdsRef.current = nextExcluded;
        setExcludedIds(nextExcluded);
      }

      if (newIds.length) {
        seenIdsRef.current = [...seenIdsRef.current, ...newIds];
        setAllSeenIds(prev => [...prev, ...newIds]);
      }

      const approvedIds = approvedApplications.filter(r => r.status === ScholarshipStatuses.Approved).map(r => r.id);
      baseListContext.onSelect(approvedIds.filter(id => !excludedIdsRef.current.includes(id)));
    }
  }, [approvedApplications]);

  return (
    <>
      {!filters && <Title title="Одобрени кандидатури" />}

      <CustomFilter
        title="Избери филтри"
        cardWidth={isMobile ? "100%" : "700px"}
        buttonLabel="Покажи"
        filters={[
          {
            label: "Тип стипендия",
            type: "select",
            value: scholarshipType ?? "",
            setValue: changeScholarshipType,
            choices: SchorashipTypeChoices,
          },
          ...(scholarshipType === ScholarshipTypes.Income
            ? [
                {
                  label: "Категория стипендия",
                  type: "select",
                  value: scholarshipCategory ?? "",
                  setValue: changeScholarshipCategory,
                  choices: ScholarshipCategoryChoices,
                },
              ]
            : []),
          {
            label: "Факултет",
            type: "select",
            value: structuralUnitId ?? "",
            setValue: setStructuralUnitId,
            choices: structuralUnits ?? [],
          },
        ]}
        buttonDisabled={!scholarshipType}
        buttonOnClick={() => handleSearch(filterQuery)}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{ display: "flex", justifyContent: "center", gap: 3 }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за одобрени кандидатури за стипендии.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <>
          {isApplicationProcessActive !== null && (
            <Typography
              sx={{
                color: isApplicationProcessActive ? "success.main" : "error.main",
                fontSize: 18,
                textAlign: "center",
                my: 2,
                fontWeight: "bold",
              }}
            >
              {isApplicationProcessActive
                ? "Процесът по кандидатстване е все още активен и резултатите не са финални!"
                : "Финални резултати, процеса по кандидатстване е неактивен!"}
            </Typography>
          )}
          {isCampaignActive ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <SecuredComponent permission={permissions.canFinalizeScholarshipCampaigns}>
                <FinalizeDialog
                  campaignId={campaignId}
                  fetchApprovedApplications={fetchApprovedApplications}
                />
              </SecuredComponent>
            </Box>
          ) : (
            <Typography
              sx={{
                color: "error.main",
                fontSize: 18,
                textAlign: "center",
                my: 2,
                fontWeight: "bold",
              }}
            >
              Кампанията е финализирана!
            </Typography>
          )}
          {scholarshipAmount && (
            <ScholarshipAmountSection
              budgetInfo={budgetInfo}
              filters={filters}
              scholarshipAmount={scholarshipAmount}
              fetchApprovedApplications={fetchApprovedApplications}
            />
          )}

          <ListContextProvider value={listContext}>
            <Title title={"Одобрени кандидатури"} />
            {isMobile ? (
              <SimpleList
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <MobileListRow
                    fields={[
                      {
                        label: "Име",
                        value: record.studentSecondName
                          ? `${record.studentFirstName} ${record.studentSecondName} ${record.studentLastName}`
                          : `${record.studentFirstName} ${record.studentLastName}`,
                      },
                    ]}
                  />
                )}
                secondaryText={record => (
                  <div>
                    <MobileListRow fields={[{ label: "Ф. номер", value: record?.facultyNumber }]} />
                    <MobileListRow fields={[{ label: "Специалност", value: record?.specialityName }]} />
                    <MobileListRow
                      fields={[
                        {
                          label: "Среден успех",
                          value: (
                            <NumberField
                              source="grade"
                              emptyText="-"
                              textAlign="left"
                            />
                          ),
                        },
                      ]}
                    />
                    <MobileListRow
                      fields={[
                        {
                          label: "Доход",
                          value: (
                            <NumberField
                              source="income"
                              emptyText="-"
                              textAlign="left"
                              options={{
                                style: "currency",
                                currency: Currency.EUR,
                                minimumFractionDigits: 2,
                              }}
                            />
                          ),
                        },
                      ]}
                    />
                    <MobileListRow
                      fields={[
                        {
                          label: "Бал",
                          value: (
                            <NumberField
                              source="rank"
                              emptyText="-"
                              textAlign="left"
                            />
                          ),
                        },
                      ]}
                    />
                    <MobileListRow
                      fields={[
                        {
                          label: "Статус",
                          value: (() => {
                            const choice = ScholarshipStatusesChoices.find(c => c.id === record?.status);
                            const textColor =
                              record?.status === ScholarshipStatuses.Approved
                                ? "success.main"
                                : record?.status === ScholarshipStatuses.Excluded
                                  ? "error.main"
                                  : "text.primary";
                            return (
                              <Typography
                                variant="body2"
                                sx={{ color: textColor, fontWeight: "bold" }}
                              >
                                {choice.name}
                              </Typography>
                            );
                          })(),
                        },
                      ]}
                    />
                    <MobileListRow fields={[{ label: "Име на кампания", value: record?.campaignName }]} />
                  </div>
                )}
              />
            ) : (
              <Datagrid
                bulkActionButtons={
                  filters?.scholarshipType === ScholarshipTypes.GradeAndIncome ? (
                    <ActionButtons
                      filters={filters}
                      setBudgetInfo={setBudgetInfo}
                      campaignId={campaignId}
                      excludedIds={excludedIds}
                      allSeenIds={allSeenIds}
                      fetchApprovedApplications={fetchApprovedApplications}
                      resetSelection={resetSelection}
                    />
                  ) : (
                    false
                  )
                }
                rowClick={false}
                sx={{
                  "& .RaDatagrid-headerCell": {
                    textAlign: "left",
                  },
                }}
              >
                <TextField
                  source="facultyNumber"
                  label="Ф. номер"
                  sortable={false}
                />
                <FunctionField
                  label="Име"
                  render={rec =>
                    rec.studentSecondName
                      ? `${rec.studentFirstName} ${rec.studentSecondName} ${rec.studentLastName}`
                      : `${rec.studentFirstName} ${rec.studentLastName}`
                  }
                />

                <TextField
                  source="specialityName"
                  label="Специалност"
                  sortable={false}
                />
                <NumberField
                  source="grade"
                  label="Среден успех"
                  emptyText="-"
                  textAlign="left"
                  sortable={false}
                />
                <NumberField
                  source="income"
                  label="Доход"
                  emptyText="-"
                  textAlign="left"
                  sortable={false}
                  options={{
                    style: "currency",
                    currency: Currency.EUR,
                    minimumFractionDigits: 2,
                  }}
                />
                <NumberField
                  source="rank"
                  label="Бал"
                  emptyText="-"
                  textAlign="left"
                  sortable={false}
                />
                <FunctionField
                  label="Статус"
                  render={record => {
                    const choice = ScholarshipStatusesChoices.find(c => c.id === record?.status);
                    const textColor =
                      record?.status === ScholarshipStatuses.Approved
                        ? "success.main"
                        : record?.status === ScholarshipStatuses.Excluded
                          ? "error.main"
                          : "text.primary";
                    return (
                      <Typography
                        variant="body2"
                        sx={{ color: textColor, fontWeight: "bold" }}
                      >
                        {choice.name}
                      </Typography>
                    );
                  }}
                />
                <TextField
                  source="campaignName"
                  label="Име на кампания"
                  sortable={false}
                />
              </Datagrid>
            )}
            <Pagination />
          </ListContextProvider>
        </>
      )}
    </>
  );
};

export default ListApprovedScholarships;
